import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common";
import { Mail, Phone, MapPin, Building2, Hash, User, ShieldCheck } from "lucide-react";
import OrderBillingPanel from "@/components/OrderBillingPanel";
import OrderTimeline from "@/components/OrderTimeline";
import OrderNextAction from "@/components/OrderNextAction";
import OrderAuditFeed from "@/components/OrderAuditFeed";
import { journeyStageLabel, isDisputed } from "@/lib/orderJourney";

export default function OrderDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [order, setOrder] = useState(null);
  const [parties, setParties] = useState(null);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    try {
      const o = await api.get(`/orders/${id}`);
      setOrder(o.data);
      const p = await api.get(`/orders/${id}/parties`);
      setParties(p.data);
    } catch (e) { setErr(e.response?.data?.detail || e.message); }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  if (!order) return <div style={{ color: "var(--ink-mute)" }}>{err || "Loading…"}</div>;

  const card = parties?.card || {};
  const cpLabel = parties?.counterparty === "supplier" ? "Awarded Supplier" :
                  parties?.counterparty === "buyer" ? "Awarded Buyer" :
                  "Counterparty";

  return (
    <>
      <PageHeader
        title={`Order ${order.code}`}
        subtitle={<span className="font-mono" style={{ color: "var(--amber-400)" }}>{order.product_name} · qty {order.quantity}</span>}
        actions={<span className={`pill pill-${isDisputed(order) ? "disputed" : order.status}`} data-testid="order-stage-pill">{journeyStageLabel(order, user.role)}</span>}
      />

      <OrderNextAction order={order} role={user.role} onChanged={load} />

      <div className="card" style={{ padding: "8px 18px 0", marginBottom: 24 }}>
        <OrderTimeline order={order} role={user.role} />
      </div>

      <div className="card" style={{ padding: 16, marginBottom: 24, borderLeft: "3px solid var(--emerald)", display: "flex", gap: 12, alignItems: "center" }}>
        <ShieldCheck size={18} color="var(--emerald)" />
        <div style={{ fontSize: 13, color: "var(--ink-dim)" }}>
          Identities revealed at award — both parties are now visible. All pre-award communication has been platform-relayed.
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18, marginBottom: 24 }} className="od-grid">
        <ContactCard title={cpLabel} card={card} accent />
        <div className="card" style={{ padding: 22 }}>
          <div className="font-mono" style={{ fontSize: 11, letterSpacing: ".1em", color: "var(--ink-mute)", marginBottom: 12 }}>ORDER FACTS</div>
          <Row icon={Hash} k="PO Code" v={<span className="font-mono" style={{ color: "var(--amber-400)" }}>{order.code}</span>} />
          <Row k="Product" v={order.product_name} />
          <Row k="Quantity" v={`${order.quantity} ${order.unit}`} />
          <Row k="Unit price" v={`$${order.unit_price.toFixed(2)} CAD`} />
          <Row k="Total" v={<b>${order.total_price.toFixed(2)} CAD</b>} />
          <Row k="Tracking" v={order.tracking_number ? `${order.tracking_number}${order.tracking_carrier ? ` · ${order.tracking_carrier}` : ""}` : "—"} />
          {order.tracking_eta && <Row k="ETA" v={new Date(order.tracking_eta).toLocaleDateString()} />}
          <Row k="Created" v={new Date(order.created_at).toLocaleString()} />
        </div>
      </div>

      <OrderBillingPanel order={order} role={user.role} onChanged={load} />

      <div className="card" style={{ padding: "18px 22px", marginTop: 24 }}>
        <OrderAuditFeed order={order} role={user.role} />
      </div>
    </>
  );
}

function ContactCard({ title, card, accent }) {
  return (
    <div className="card" style={{ padding: 22, borderLeft: accent ? "3px solid var(--amber-500)" : undefined }}>
      <div className="font-mono" style={{ fontSize: 11, letterSpacing: ".1em", color: "var(--ink-mute)", marginBottom: 12 }}>{title.toUpperCase()}</div>
      <div className="font-serif-display" style={{ fontSize: 22, fontWeight: 600 }} data-testid="party-name">{card.legal_name || "—"}</div>
      <div style={{ marginTop: 14, display: "grid", gap: 8 }}>
        <Line icon={User} v={card.contact_name || <em style={{ color: "var(--ink-mute)" }}>Contact name not provided</em>} />
        <Line icon={Mail} v={card.contact_email
          ? <a href={`mailto:${card.contact_email}`} style={{ color: "var(--amber-400)" }}>{card.contact_email}</a>
          : <em style={{ color: "var(--ink-mute)" }}>Email not provided</em>} />
        <Line icon={Phone} v={card.contact_phone || <em style={{ color: "var(--ink-mute)" }}>Phone not provided</em>} />
        <Line icon={MapPin} v={card.address
          ? `${card.address}, ${card.city || ""} ${card.province || ""} ${card.postal_code || ""}`
          : <em style={{ color: "var(--ink-mute)" }}>Address not provided</em>} />
        {card.warehouse_address && <Line icon={Building2} v={`Warehouse: ${card.warehouse_address}`} />}
        {card.tax_number && <Line icon={Hash} v={`Tax #: ${card.tax_number}`} />}
      </div>
    </div>
  );
}
function Line({ icon: Icon, v }) { return <div style={{ display: "flex", gap: 10, alignItems: "center", fontSize: 14 }}><Icon size={14} color="var(--ink-mute)" /> {v}</div>; }
function Row({ icon: Icon, k, v }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "120px 1fr", gap: 10, padding: "8px 0", borderBottom: "1px dashed var(--line)" }}>
      <div className="font-mono" style={{ fontSize: 11, color: "var(--ink-mute)", textTransform: "uppercase", letterSpacing: ".08em" }}>{Icon ? <Icon size={11} style={{ verticalAlign: -1 }} /> : null} {k}</div>
      <div style={{ fontSize: 14 }}>{v}</div>
    </div>
  );
}
